import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Label } from '@/components/ui-kit/label';
import { LEAVE_DOCUMENT_ACCEPT, addLeaveDocuments } from '../../utils/leave-documents';

export interface SickLeaveDocumentFieldProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** The holder of the doctor rule may say the doctor sends the document instead. */
  canDoctorSend: boolean;
  doctorSends: boolean;
  onDoctorSendsChange: (doctorSends: boolean) => void;
  /** The sick leave certificate rule asks for a document (with the minimum working days). */
  requiredDays: number | null;
  /** Shown after a submit without the required document. */
  error?: string;
  disabled?: boolean;
}

/** A sick leave's doctor's document: upload up to 5 files, or (doctor rule) tick that the doctor sends it. */
export const SickLeaveDocumentField = ({
  files,
  onFilesChange,
  canDoctorSend,
  doctorSends,
  onDoctorSendsChange,
  requiredDays,
  error,
  disabled,
}: SickLeaveDocumentFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState('');

  const addFiles = (list: FileList | null | undefined) => {
    if (!list?.length) return;
    const result = addLeaveDocuments(files, Array.from(list));
    setFileError(result.error ?? '');
    if (result.files) onFilesChange(result.files);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="sick-leave-documents">{t('UPLOAD_PRESCRIPTION')}</Label>
      {requiredDays !== null && (
        <p className="text-sm text-muted-foreground">
          {t('SICK_LEAVE_DOCUMENT_REQUIRED_HINT', { days: requiredDays })}
        </p>
      )}

      {canDoctorSend && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={doctorSends}
            disabled={disabled}
            onCheckedChange={(value) => {
              onDoctorSendsChange(value === true);
              // The doctor sends it: nothing uploaded is kept (legacy).
              if (value === true) onFilesChange([]);
            }}
          />
          {t('PRESCRIPTION_SEND_BY_DOCTOR')}
        </label>
      )}

      {!doctorSends && (
        <>
          <input
            ref={inputRef}
            id="sick-leave-documents"
            type="file"
            multiple
            accept={LEAVE_DOCUMENT_ACCEPT}
            className="sr-only"
            disabled={disabled}
            onChange={(event) => addFiles(event.target.files)}
          />
          <label
            htmlFor="sick-leave-documents"
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(event.dataTransfer.files);
            }}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 py-4 text-center transition-colors hover:border-primary hover:bg-accent',
              dragging && 'border-primary bg-primary/5'
            )}
          >
            <Upload className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm">{t('SELECT_SICK_LEAVE_FILE_MESSAGE')}</span>
            <span className="text-xs text-muted-foreground">{t('SICK_LEAVE_FILES_HINT')}</span>
          </label>

          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-3 rounded-md border bg-card px-3 py-2"
            >
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-sm" title={file.name}>
                {file.name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('REMOVE')}
                disabled={disabled}
                onClick={() => {
                  setFileError('');
                  onFilesChange(files.filter((_, i) => i !== index));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </>
      )}

      {(fileError || error) && <p className="text-sm text-destructive">{t(fileError || error || '')}</p>}
    </div>
  );
};
